import { useEffect, useState } from "react";

import PropTypes from "prop-types";

import { ChangeCircle } from "@mui/icons-material";
import { Box, IconButton, InputAdornment } from "@mui/material";

import { DiscountTypeEnum, formatMoney } from "../../shared";
import { CustomNumberField } from "../custom-number-field";

export const CustomDiscountField = ({
  value,
  discountType,
  onTypeChanged,
  baseAmount,
  helperText,
  inputProps,
  disabled = false,
  ...props
}) => {
  // State to manage discount type
  const [calculatedHelperText, setCalculatedHelperText] = useState("");

  // Toggle between FIXED_AMOUNT and PERCENTAGE types
  const toggleDiscountType = () => {
    const newType =
      discountType === DiscountTypeEnum.FIXED_AMOUNT
        ? DiscountTypeEnum.PERCENTAGE
        : DiscountTypeEnum.FIXED_AMOUNT;
    if (onTypeChanged) onTypeChanged(newType);
  };

  // Update helper text when discount type is percentage and value is provided
  useEffect(() => {
    if (discountType === DiscountTypeEnum.PERCENTAGE && value) {
      const calculatedAmount = ((baseAmount * value) / 100).toFixed(2);
      setCalculatedHelperText(`~ ${formatMoney(calculatedAmount)}`);
    } else {
      setCalculatedHelperText(""); // Clear helper text when type is fixed or no discount amount
    }
  }, [discountType, value, baseAmount]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: helperText || calculatedHelperText ? "center" : "end",
      }}
    >
      <CustomNumberField
        {...props}
        value={value}
        disabled={disabled}
        InputProps={{
          startAdornment:
            discountType === DiscountTypeEnum.FIXED_AMOUNT ? (
              <InputAdornment position="start">Rp</InputAdornment>
            ) : null,
          endAdornment:
            discountType === DiscountTypeEnum.PERCENTAGE ? (
              <InputAdornment position="end">%</InputAdornment>
            ) : null,
        }}
        inputProps={{
          style: { textAlign: "right" },
          ...inputProps,
        }}
        helperText={helperText || calculatedHelperText}
        FormHelperTextProps={{
          sx: {
            textAlign: "right",
          },
        }}
      />
      <IconButton
        onClick={toggleDiscountType}
        aria-label="Toggle Discount Type"
        disabled={disabled}
      >
        <ChangeCircle />
      </IconButton>
    </Box>
  );
};

CustomDiscountField.propTypes = {
  value: PropTypes.number,
  discountType: PropTypes.oneOf([
    DiscountTypeEnum.FIXED_AMOUNT,
    DiscountTypeEnum.PERCENTAGE,
  ]),
  onTypeChanged: PropTypes.func,
  baseAmount: PropTypes.number.isRequired,
  helperText: PropTypes.string,
  inputProps: PropTypes.object,
  disabled: PropTypes.bool,
};
